import type {
  ScheduleCommentScrollHandler,
  ScreenplayCommentGoToHandler,
  ScreenplayCommentGoToRequest,
} from "./projectComments.types";

type BridgeEntry = {
  screenplayGoTo: ScreenplayCommentGoToHandler | null;
  scheduleScroll: ScheduleCommentScrollHandler | null;
};

function scopeKey(projectId: string, documentId: string): string {
  return `${projectId}:${documentId}`;
}

const entries = new Map<string, BridgeEntry>();
const pendingScreenplayGoTo = new Map<string, ScreenplayCommentGoToRequest>();
const pendingScheduleScrollTargetId = new Map<string, string>();

function getEntry(projectId: string, documentId: string): BridgeEntry {
  const key = scopeKey(projectId, documentId);
  const existing = entries.get(key);
  if (existing) return existing;

  const created: BridgeEntry = {
    screenplayGoTo: null,
    scheduleScroll: null,
  };
  entries.set(key, created);
  return created;
}

export function setProjectCommentsScreenplayGoToHandler(
  projectId: string,
  documentId: string,
  handler: ScreenplayCommentGoToHandler | null
): void {
  const key = scopeKey(projectId, documentId);
  const entry = getEntry(projectId, documentId);
  entry.screenplayGoTo = handler;

  if (!handler) return;

  const pending = pendingScreenplayGoTo.get(key);
  if (!pending) return;

  pendingScreenplayGoTo.delete(key);
  handler(pending.paragraphId, pending.sceneId);
}

export function setProjectCommentsScheduleScrollHandler(
  projectId: string,
  documentId: string,
  handler: ScheduleCommentScrollHandler | null
): void {
  const key = scopeKey(projectId, documentId);
  const entry = getEntry(projectId, documentId);
  entry.scheduleScroll = handler;

  if (!handler) return;

  const pendingTargetId = pendingScheduleScrollTargetId.get(key);
  if (!pendingTargetId) return;

  pendingScheduleScrollTargetId.delete(key);
  handler(pendingTargetId);
}

export function invokeProjectCommentsScreenplayGoTo(
  projectId: string,
  documentId: string,
  request: ScreenplayCommentGoToRequest,
  onNavigate?: () => void
): void {
  const key = scopeKey(projectId, documentId);
  const handler = getEntry(projectId, documentId).screenplayGoTo;

  if (handler) {
    handler(request.paragraphId, request.sceneId);
    return;
  }

  pendingScreenplayGoTo.set(key, request);
  onNavigate?.();
}

export function invokeProjectCommentsScheduleScroll(
  projectId: string,
  documentId: string,
  targetId: string,
  onNavigate?: () => void
): void {
  const key = scopeKey(projectId, documentId);
  const handler = getEntry(projectId, documentId).scheduleScroll;

  if (handler) {
    handler(targetId);
    return;
  }

  pendingScheduleScrollTargetId.set(key, targetId);
  onNavigate?.();
}

export function clearProjectCommentsBridge(projectId: string, documentId: string): void {
  const key = scopeKey(projectId, documentId);
  entries.delete(key);
  pendingScreenplayGoTo.delete(key);
  pendingScheduleScrollTargetId.delete(key);
}
